import { useEffect, useState } from 'react'
import { ArrowDown, ArrowUp, Check, Plus, Trash2, X } from 'lucide-react'
import { isManager, useAuth } from '@/contexts/AuthContext'
import { useLanguage } from '@/contexts/LanguageContext'
import EmojiGlyph from '@/components/ui/EmojiGlyph'
import EmojiPicker from '@/components/settings/EmojiPicker'
import SettingsSection, {
  SettingsEmpty, SettingsSkeleton,
  settingsAddButton, settingsAddRow, settingsIconButton, settingsInput, settingsRow,
} from '@/components/settings/SettingsSection'
import {
  setRosterCatalog, useRosterCatalog,
  type RosterCatalog, type RosterRole, type SquadRule, type SquadType,
} from '@/lib/rosterCatalog'

/** A key for an entry that does not exist on the server yet. */
const mintKey = (prefix: string) => `${prefix}_${Math.random().toString(36).slice(2, 8)}`

function moved<T>(list: T[], i: number, dir: -1 | 1): T[] {
  const j = i + dir
  if (j < 0 || j >= list.length) return list
  const next = [...list]
  ;[next[i], next[j]] = [next[j], next[i]]
  return next
}

const numberInput = `${settingsInput} w-12 px-1 text-center tabular-nums`

/**
 * Settings → Kadro Rolleri + Takım Türleri: the vocabulary rosters are built
 * from. One draft, saved as one document — a type's rules name roles, so the
 * two lists must never be saved half-way apart.
 */
export default function RosterCatalogSettings() {
  const { user } = useAuth()
  const { T } = useLanguage()
  const canEdit = isManager(user)
  const live = useRosterCatalog()

  const [draft, setDraft] = useState<RosterCatalog | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [justSaved, setJustSaved] = useState(false)
  const [newRoleName, setNewRoleName] = useState('')
  const [newRoleEmoji, setNewRoleEmoji] = useState('')
  const [newTypeName, setNewTypeName] = useState('')
  const [newTypeEmoji, setNewTypeEmoji] = useState('')

  // Take the catalog as the draft once it arrives (and after each save).
  useEffect(() => {
    if (live.roles.length && !draft) setDraft(live)
  }, [live, draft])

  if (!draft) {
    return (
      <SettingsSection title={T.settings.rosterRoles} description={T.settings.rosterRolesDesc}>
        <SettingsSkeleton rows={4} />
      </SettingsSection>
    )
  }

  const dirty = JSON.stringify(draft) !== JSON.stringify(live)

  // Checked before saving, so nothing the user can see is dropped silently —
  // deleting a role strips it from every type, and a type with no roles left
  // would otherwise vanish on save (or fail it, when it was the last one).
  const namedRoles = new Set(draft.roles.filter(r => r.name.trim()).map(r => r.key))
  const problems: string[] = []
  if (!namedRoles.size) problems.push(T.settings.rosterProblemNoRoles)
  if (draft.roles.some(r => !r.name.trim())) problems.push(T.settings.rosterProblemRoleName)
  if (!draft.squad_types.length) problems.push(T.settings.rosterProblemNoTypes)
  for (const t of draft.squad_types) {
    if (!t.name.trim()) problems.push(T.settings.rosterProblemTypeName)
    else if (!t.rules.some(r => namedRoles.has(r.role))) {
      problems.push(T.settings.rosterProblemTypeNoRoles.replace('{name}', t.name.trim()))
    }
  }
  const edit = (next: RosterCatalog) => { setDraft(next); setJustSaved(false) }

  // ── Roles ──
  const setRole = (i: number, patch: Partial<RosterRole>) =>
    edit({ ...draft, roles: draft.roles.map((r, j) => (j === i ? { ...r, ...patch } : r)) })

  const removeRole = (i: number) => {
    const key = draft.roles[i].key
    // A rule cannot outlive its role. A type left with no rules is flagged
    // below and blocks the save until it gets a role or is deleted.
    edit({
      roles: draft.roles.filter((_, j) => j !== i),
      squad_types: draft.squad_types.map(t => ({ ...t, rules: t.rules.filter(r => r.role !== key) })),
    })
  }

  const addRole = () => {
    if (!newRoleName.trim()) return
    edit({ ...draft, roles: [...draft.roles, { key: mintKey('r'), name: newRoleName.trim(), emoji: newRoleEmoji }] })
    setNewRoleName('')
    setNewRoleEmoji('')
  }

  const usedBy = (key: string) => draft.squad_types.filter(t => t.rules.some(r => r.role === key)).length

  // ── Squad types ──
  const setType = (i: number, patch: Partial<SquadType>) =>
    edit({ ...draft, squad_types: draft.squad_types.map((t, j) => (j === i ? { ...t, ...patch } : t)) })

  const setRule = (ti: number, ri: number, patch: Partial<SquadRule>) => {
    const t = draft.squad_types[ti]
    const rules = t.rules.map((r, j) => {
      if (j !== ri) return r
      const next = { ...r, ...patch }
      // Keep min ≤ default ≤ max whichever field moved.
      next.min = Math.max(0, Math.min(next.min, 20))
      next.max = Math.max(next.min, Math.min(next.max, 20))
      next.default = Math.max(next.min, Math.min(next.default, next.max))
      return next
    })
    setType(ti, { rules })
  }

  const addRule = (ti: number) => {
    const t = draft.squad_types[ti]
    const free = draft.roles.find(r => !t.rules.some(x => x.role === r.key))
    if (!free) return
    setType(ti, { rules: [...t.rules, { role: free.key, min: 0, max: 4, default: 1 }] })
  }

  const addType = () => {
    if (!newTypeName.trim() || !draft.roles.length) return
    edit({
      ...draft,
      squad_types: [...draft.squad_types, {
        key: mintKey('t'),
        name: newTypeName.trim(),
        emoji: newTypeEmoji,
        rules: [{ role: draft.roles[0].key, min: 1, max: 1, default: 1 }],
      }],
    })
    setNewTypeName('')
    setNewTypeEmoji('')
  }

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      const res = await fetch('/api/roster-catalog', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draft),
      })
      if (!res.ok) {
        const d = await res.json().catch(() => ({}))
        setError(d.detail ?? T.settings.rosterCatalogSaveError)
        return
      }
      const saved: RosterCatalog = await res.json()
      setRosterCatalog(saved)   // every open roster screen picks it up
      setDraft(saved)
      setJustSaved(true)
    } catch {
      setError(T.settings.rosterCatalogSaveError)
    } finally {
      setBusy(false)
    }
  }

  const saveBar = canEdit && (
    <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
      {problems.length > 0 && (
        <ul className="mr-auto space-y-0.5 text-xs text-amber-400/90">
          {problems.map(p => <li key={p}>• {p}</li>)}
        </ul>
      )}
      {error && <p className="mr-auto text-xs text-destructive">{error}</p>}
      {justSaved && !dirty && (
        <span className="mr-auto flex items-center gap-1 text-xs text-green-400/80">
          <Check className="h-3.5 w-3.5" />{T.settings.rosterDefaultSaved}
        </span>
      )}
      {dirty && (
        <button onClick={() => { setDraft(live); setError(null) }}
          className="flex h-8 items-center rounded px-3 text-xs text-muted-foreground transition-colors hover:bg-white/8 hover:text-foreground">
          {T.settings.rosterCatalogDiscard}
        </button>
      )}
      <button onClick={save} disabled={busy || !dirty || problems.length > 0} className={settingsAddButton}>
        {busy ? T.settings.rosterDefaultSaving : T.settings.rosterDefaultSave}
      </button>
    </div>
  )

  return (
    <>
      {/* ── Roles ─────────────────────────────────────────────────────────── */}
      <SettingsSection title={T.settings.rosterRoles} description={T.settings.rosterRolesDesc}>
        {draft.roles.length === 0 ? (
          <SettingsEmpty>{T.settings.rosterRolesEmpty}</SettingsEmpty>
        ) : (
          <div className="space-y-2">
            {draft.roles.map((role, i) => {
              const n = usedBy(role.key)
              return (
                <div key={role.key} className={settingsRow}>
                  {canEdit
                    ? <EmojiPicker value={role.emoji} onChange={emoji => setRole(i, { emoji })} />
                    : <span className="flex w-8 justify-center"><EmojiGlyph emoji={role.emoji} size={18} /></span>}
                  <input
                    value={role.name}
                    disabled={!canEdit}
                    maxLength={40}
                    onChange={e => setRole(i, { name: e.target.value })}
                    className={`${settingsInput} min-w-0 flex-1 disabled:opacity-80`}
                  />
                  {n > 0 && (
                    <span className="hidden shrink-0 text-[11px] text-muted-foreground/50 sm:inline">
                      {T.settings.rosterRoleUsedBy.replace('{n}', String(n))}
                    </span>
                  )}
                  {canEdit && (
                    <div className="flex items-center">
                      <button className={settingsIconButton} disabled={i === 0}
                        onClick={() => edit({ ...draft, roles: moved(draft.roles, i, -1) })} aria-label="up">
                        <ArrowUp className="h-3.5 w-3.5" />
                      </button>
                      <button className={settingsIconButton} disabled={i === draft.roles.length - 1}
                        onClick={() => edit({ ...draft, roles: moved(draft.roles, i, 1) })} aria-label="down">
                        <ArrowDown className="h-3.5 w-3.5" />
                      </button>
                      <button className={`${settingsIconButton} hover:!text-destructive`} onClick={() => removeRole(i)} aria-label="delete">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {canEdit && (
          <div className={settingsAddRow}>
            <EmojiPicker value={newRoleEmoji} onChange={setNewRoleEmoji} />
            <input
              value={newRoleName}
              maxLength={40}
              onChange={e => setNewRoleName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addRole() }}
              placeholder={T.settings.rosterRoleNamePh}
              className={`${settingsInput} min-w-0 flex-1`}
            />
            <button onClick={addRole} disabled={!newRoleName.trim()} className={settingsAddButton}>
              <Plus className="h-3.5 w-3.5" />{T.settings.rosterAdd}
            </button>
          </div>
        )}
        {saveBar}
      </SettingsSection>

      {/* ── Squad types ───────────────────────────────────────────────────── */}
      <SettingsSection title={T.settings.squadTypes} description={T.settings.squadTypesDesc}>
        <div className="space-y-3">
          {draft.squad_types.map((t, ti) => (
            <div key={t.key} className="rounded-sm border border-white/10 bg-white/[0.035]">
              <div className="flex items-center gap-3 border-b border-white/10 px-3 py-2">
                {canEdit
                  ? <EmojiPicker value={t.emoji} onChange={emoji => setType(ti, { emoji })} />
                  : <span className="flex w-8 justify-center"><EmojiGlyph emoji={t.emoji} size={18} /></span>}
                <input
                  value={t.name}
                  disabled={!canEdit}
                  maxLength={40}
                  onChange={e => setType(ti, { name: e.target.value })}
                  className={`${settingsInput} min-w-0 flex-1 font-semibold disabled:opacity-80`}
                />
                {canEdit && (
                  <div className="flex items-center">
                    <button className={settingsIconButton} disabled={ti === 0}
                      onClick={() => edit({ ...draft, squad_types: moved(draft.squad_types, ti, -1) })} aria-label="up">
                      <ArrowUp className="h-3.5 w-3.5" />
                    </button>
                    <button className={settingsIconButton} disabled={ti === draft.squad_types.length - 1}
                      onClick={() => edit({ ...draft, squad_types: moved(draft.squad_types, ti, 1) })} aria-label="down">
                      <ArrowDown className="h-3.5 w-3.5" />
                    </button>
                    <button className={`${settingsIconButton} hover:!text-destructive`}
                      onClick={() => edit({ ...draft, squad_types: draft.squad_types.filter((_, j) => j !== ti) })} aria-label="delete">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-1.5 px-3 py-2.5">
                {t.rules.length > 0 && (
                  <div className="grid grid-cols-[1fr_auto_auto_auto_1.75rem] items-center gap-x-2 text-[10px] uppercase tracking-widest text-muted-foreground/40">
                    <span>{T.settings.squadTypeRole}</span>
                    <span className="w-12 text-center">{T.settings.squadTypeMin}</span>
                    <span className="w-12 text-center">{T.settings.squadTypeDefault}</span>
                    <span className="w-12 text-center">{T.settings.squadTypeMax}</span>
                    <span />
                  </div>
                )}
                {t.rules.length === 0 && (
                  <p className="text-xs text-amber-400/80">{T.settings.squadTypeNoRules}</p>
                )}
                {canEdit && t.rules.length === 0 && draft.roles.length > 0 && (
                  <button onClick={() => addRule(ti)} className={`${settingsAddButton} mt-1`}>
                    <Plus className="h-3.5 w-3.5" />{T.settings.squadTypeAddRule}
                  </button>
                )}
                {t.rules.map((rule, ri) => (
                  <div key={ri} className="grid grid-cols-[1fr_auto_auto_auto_1.75rem] items-center gap-x-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <EmojiGlyph emoji={draft.roles.find(r => r.key === rule.role)?.emoji} size={16} />
                      <select
                        value={rule.role}
                        disabled={!canEdit}
                        onChange={e => setRule(ti, ri, { role: e.target.value })}
                        className={`${settingsInput} min-w-0 flex-1 bg-card disabled:opacity-80`}
                      >
                        {draft.roles
                          .filter(r => r.key === rule.role || !t.rules.some(x => x.role === r.key))
                          .map(r => <option key={r.key} value={r.key}>{r.name}</option>)}
                      </select>
                    </div>
                    {(['min', 'default', 'max'] as const).map(field => (
                      <input
                        key={field}
                        type="number"
                        min={0}
                        max={20}
                        disabled={!canEdit}
                        value={rule[field]}
                        onChange={e => setRule(ti, ri, { [field]: Number(e.target.value) || 0 })}
                        className={`${numberInput} disabled:opacity-80`}
                      />
                    ))}
                    {canEdit ? (
                      <button className={`${settingsIconButton} hover:!text-destructive`}
                        onClick={() => setType(ti, { rules: t.rules.filter((_, j) => j !== ri) })} aria-label="remove">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    ) : <span />}
                  </div>
                ))}
                {canEdit && t.rules.length < draft.roles.length && (
                  <button onClick={() => addRule(ti)}
                    className="flex items-center gap-1 pt-1 text-xs text-muted-foreground/60 transition-colors hover:text-foreground">
                    <Plus className="h-3 w-3" />{T.settings.squadTypeAddRule}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {canEdit && (
          <div className={settingsAddRow}>
            <EmojiPicker value={newTypeEmoji} onChange={setNewTypeEmoji} />
            <input
              value={newTypeName}
              maxLength={40}
              onChange={e => setNewTypeName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addType() }}
              placeholder={T.settings.squadTypeNamePh}
              className={`${settingsInput} min-w-0 flex-1`}
            />
            <button onClick={addType} disabled={!newTypeName.trim() || !draft.roles.length} className={settingsAddButton}>
              <Plus className="h-3.5 w-3.5" />{T.settings.rosterAdd}
            </button>
          </div>
        )}
        {saveBar}
      </SettingsSection>
    </>
  )
}
